import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnDestroy,
  Inject,
} from '@angular/core';
import { MatHorizontalStepper } from '@angular/material/stepper';
import { FormBuilder, FormGroup } from '@angular/forms';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ProductOffering } from 'anchor-shared/lib/model/entity/productOffering';
import { unsubscribeFromAll } from '../../../../../../../../../core/pure-functions/rx-helper';
import { ContainsSubscriptions } from '../../../../../../../../../core/services/interfaces/contains-subscriptions';
import { ProductOfferingDAO } from '../../../../../../../../../core/services/dao/productOfferingDAO';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Vessel } from 'anchor-shared/lib/model/entity/vessel';
import { VesselAvailabilityPreferencesDAO } from '../../../../../../../../../core/services/dao/vesselAvailabilityPreferencesDAO';
import { take } from 'rxjs/operators';
import { VesselAvailabilityDAO } from '../../../../../../../../../core/services/dao/VesselAvailabilityDAO';

@Component({
  selector: 'app-product-offering-form',
  templateUrl: './product-offering-form.component.html',
  styleUrls: ['./product-offering-form.component.scss'],
})
export class ProductOfferingFormComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  @Input()
  vessel: Vessel;

  @Input()
  existingProductToUpdate: Partial<ProductOffering> = null;

  @Output()
  resetEditState = new EventEmitter<any>();

  @Output()
  updateProductOfferings = new EventEmitter<Partial<ProductOffering>>();

  savingSpinner = false;

  public productOffering: BehaviorSubject<Partial<ProductOffering>> =
    new BehaviorSubject<Partial<ProductOffering>>({
      availabilityPreferencesId: null,
      productName: null,
      hoursBetween: 1,
      duration: null,
      active: null,
      days: null,
      openAvailability: null,
      openAvailabilityStartTime: null,
      openAvailabilityEndTime: null,
      customAvailability: false,
      customAvailabilityTimes: null,
      discount: null,
      flexTimes: null,
    });
  productDurationFormGroup: FormGroup;
  subs: Subscription[] = [];
  isMobile: boolean;

  constructor(
    public dialogRef: MatDialogRef<ProductOfferingFormComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: any,
    private _fb: FormBuilder,
    private productOfferingDAO: ProductOfferingDAO,
    private vesselAvailabilityPreferencesDAO: VesselAvailabilityPreferencesDAO,
    private vesselAvailabilityDAO: VesselAvailabilityDAO,
  ) {
    this.isMobile = window && window.screen && window.screen.width < 960;
    if (this.data && this.data.vessel) {
      this.vessel = this.data.vessel;
      if (this.data.productOffering) {
        this.productOffering.next(this.data.productOffering);
      }
    }
  }

  ngOnInit() {
    if (this.existingProductToUpdate) {
      this.productOffering.next(this.existingProductToUpdate);
    } else {
      this.productOffering.next({
        ...this.productOffering.value,
        availabilityPreferencesId: this.vessel.vesselAvailabilityPreferences.id,
      });
    }
  }

  ngOnDestroy() {
    unsubscribeFromAll(this.subs);
  }

  moveStepper(stepper: MatHorizontalStepper, direction: string) {
    if (direction === 'next') {
      stepper.next();
    } else {
      stepper.previous();
    }
  }

  daySelected(): boolean {
    if (this.productOffering.value.days) {
      for (let day in this.productOffering.value.days) {
        if (this.productOffering.value.days[day]) {
          return true;
        }
      }
    }
    return false;
  }

  saveProductOffering() {
    const conflictCheckSub = this.productOfferingDAO
      .activeProductConflicts(
        this.vessel.id,
        this.vessel.vesselAvailabilityPreferences.id,
        this.productOffering.value,
      )
      .subscribe((conflicts) => {
        if (conflicts && conflicts.length > 0) {
          this.setProductOfferingStateAfterConflictCheck(false);
        } else {
          this.setProductOfferingStateAfterConflictCheck(true);
        }
        this.savingSpinner = true;
        if (this.productOffering.value.id) {
          this.updateProduct();
        } else {
          this.createProductOffering();
        }
        const prefsSub = this.vesselAvailabilityPreferencesDAO
          .update(
            this.vessel.id,
            this.vessel.vesselAvailabilityPreferences.id,
            {
              advanceNoticeMinHours:
                this.vessel.vesselAvailabilityPreferences.advanceNoticeMinHours,
            },
          )
          .subscribe();
        this.subs.push(prefsSub);
      });
    this.subs.push(conflictCheckSub);
  }

  private setProductOfferingStateAfterConflictCheck(state: boolean) {
    this.productOffering.next({
      ...this.productOffering.value,
      active: state,
    });
  }

  closeForm(cancelEdit): void {
    if (this.isMobile) {
      let response: any = {
        product: this.productOffering.value,
      };
      if (cancelEdit) {
        this.dialogRef.close();
      } else {
        this.dialogRef.close(response);
      }
    } else {
      this.resetEditState.emit();
    }
  }

  selectedStartTime() {
    if (
      this.productOffering.value.openAvailability &&
      this.productOffering.value.openAvailabilityStartTime &&
      this.productOffering.value.openAvailabilityEndTime
    ) {
      return true;
    } else if (this.productOffering.value.customAvailability) {
      return true;
    }
    return false;
  }

  selectedTimeWindows() {
    if (this.productOffering.value.customAvailabilityTimes) {
      if (this.productOffering.value.customAvailabilityTimes.length > 0) {
        return true;
      }
    }
    return false;
  }

  disableDiscountContinue(): boolean {
    return (
      isNaN(this.productOffering.value.discount) ||
      this.productOffering.value.discount < 0 ||
      this.productOffering.value.discount > 100
    );
  }

  private createProductOffering() {
    const saveProductSub = this.productOfferingDAO
      .createProductOffering(this.vessel.id, this.productOffering.value)
      .subscribe((product) => {
        this.productOffering.value.id = product.id;
        this.updateVesselAvailability();
      });
    this.subs.push(saveProductSub);
  }

  private updateProduct() {
    const updateProductSub = this.productOfferingDAO
      .update(
        this.vessel.id,
        this.productOffering.value.id,
        this.productOffering.value,
      )
      .subscribe((product) => {
        this.updateVesselAvailability();
      });
    this.subs.push(updateProductSub);
  }
  private updateVesselAvailability() {
    this.vesselAvailabilityDAO
      .updateFullAvailabilitiesForVessel(this.vessel.id)
      .pipe(take(1))
      .subscribe(() => {
        this.savingSpinner = false;
        this.updateProductOfferings.emit(this.productOffering.value);
        this.existingProductToUpdate = null;
        this.closeForm(false);
      });
  }
}
